import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "tailwindcss/tailwind.css";
import {
  Text,
  TextInput,
  Select,
  Button,
  ActionIcon,
  Badge,
  RangeSlider
} from "@mantine/core";
import UniversalTable from "../../components/Common/UniversalTable";
import TablePagination from "../../components/Common/TablePagination";
import { Search, SortAscending, Download } from "tabler-icons-react";
import { DateRangePicker } from "@mantine/dates";
import { Eye } from "tabler-icons-react";
import ExcelJS from "exceljs";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { fetchRecoveryDetailApi } from "../../utilities/apis/recovery";
import { formatDate, formatNumberWithCommas } from "../../utilities/utilities";
import { useSelector } from "react-redux";
import { selectUserData } from "../../redux/selectors";

export default function RecoveryListingPage() {
  const currentUser = useSelector(selectUserData) || {};

  const currentPath = location.pathname;
  const navigate = useNavigate();
  const [recoveryList, setRecoveryList] = useState([]);
  const [recoveryData, setRecoveryData] = useState({
    filter: {
      searchText: "",
      nextActionDate: "",
      lastActionDate: "",
      sortType: "DESC",
      orderByColumn: "nextAction",
      minAmount: 0,
      maxAmount: 0,
      roleId: "",
      roleToCompare: "",
      isRecoveryLocker: false,
      apiCall: 0,
    },
    pageNo: 1,
    pageSize: '10',
    count: 0,
  });

  const fetchRecoverys = async () => {
    if (currentPath === "/app/recovery/locker") {
      recoveryData.filter.roleId = "";
      recoveryData.filter.roleToCompare = "";
      recoveryData.filter.isRecoveryLocker = true;
    } else {
      recoveryData.filter.roleId = currentUser.id;
      recoveryData.filter.roleToCompare = currentUser.loginAs.name;
      recoveryData.filter.isRecoveryLocker = false;
    }
    const response = await apiWrapWithErrorWithData(
      fetchRecoveryDetailApi({ recoveryData })
    );
    if (response.success) {
      console.log('this is recovery data', response.data);
      if (response.data.length > 0) {
        recoveryData.count = response.data[0].totalCount;
      }
      setRecoveryList(response.data);
    }
  };
  useEffect(() => {
    fetchRecoverys();
  }, [recoveryData.filter.apiCall, currentPath]);

  const handleSearchClick = () => {
    fetchRecoverys();
  };

  const handleClearClick = async () => {
    setRecoveryData({
      ...recoveryData,
      filter: {
        ...recoveryData.filter,
        searchText: "",
        nextActionDate: "",
        lastActionDate: "",
        minAmount: 0,
        maxAmount: 0,
        apiCall: recoveryData.filter.apiCall + 1,
      },
    });
    fetchRecoverys();
  };

  const handleExcelDownload = async () => {
    const mappedData = recoveryList.map(item => ({
      "Ref no": `recovery-${item.id}`,
      "Amount": `${item.currencyType === "dollar" ? "$" : "₹"} ${item.recoveryAmount}`,
      "Debtor Name": item.name,
      "Next Action": item.nextAction ? `${item.nextAction.actionCount} | ${formatDate(item.nextAction.executionDate)}` : "",
      "Last Action": item.lastAction ? `${item.lastAction.actionCount} | ${formatDate(item.lastAction.executionDate)}` : "",
      "Status": item.nextAction ? item.nextAction.isPaused ? "Paused" : "Active" : "Active",
    }));
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');
    worksheet.columns = [
      { header: 'Ref no.', key: 'Ref no', width: 15 },
      { header: 'Amount', key: 'Amount', width: 15 },
      { header: 'Debtor Name', key: 'Debtor Name', width: 15 },
      { header: 'Next Action', key: 'Next Action', width: 15 },
      { header: 'Last Action', key: 'Last Action', width: 15 },
      { header: 'Status', key: 'Status', width: 15 },
    ];
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber <= 6) {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFADD8E6' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      }
    });
    mappedData.forEach((data) => {
      const row = worksheet.addRow(data);
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument-spreadsheetml.sheet' });
    saveAs(blob, 'recoveryList.xlsx');
  };

  const recoveryTableHeaders = [
    { label: 'Reference No.', key: 'id' },
    {
      label: 'Amount',
      key: 'recoveryAmount',
      render: (row) => (
        <Text size="sm" weight={500} className="text-slate-700">
          {`${row.currencyType === 'dollar' ? '$' : '₹'} ${row.recoveryAmount}`}
        </Text>
      ),
    },
    { label: 'Debtor Name', key: 'name' },
    {
      label: 'Next Action',
      key: 'nextAction',
      render: (row) => (
        <Text size="sm">
          {row.nextAction ? `${row.nextAction.actionCount} | ${formatDate(row.nextAction.executionDate)}` : ''}
        </Text>
      ),
    },
    {
      label: 'Last Action',
      key: 'lastAction',
      render: (row) => (
        <Text size="sm">
          {row.lastAction ? `${row.lastAction.actionCount} | ${formatDate(row.lastAction.executionDate)}` : ''}
        </Text>
      ),
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => (
        // <Badge color={row.nextAction ? (row.nextAction.isPaused ? 'red' : 'blue') : 'blue'}>
        <Badge>
          {row.nextAction ? (row.nextAction.isPaused ? 'Paused' : 'Active') : 'Active'}
        </Badge>
      ),
    },
    {
      label: 'Action',
      key: 'actions',
      render: (row) => (
        <ActionIcon
          color="#1b6f8d"
          title="View"
          onClick={() => navigate(`/app/recovery/${row.id}`)}
        >
          <Eye />
        </ActionIcon>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between my-4 mb-8">
          <Text
            className="font-normal text-[20px] leading-[1.55] text-black"
            style={{ color: "#1b6f8d" }}>
            recoveries list
          </Text>
        </div>
      </div>
      <div className="flex w-full justify-between items-center">
        <div className="flex flex-col">
          <div className="flex flex-row-sm">
            <div className="flex items-center">
              {/* <Select
                className="mx-2 min-w-[225px]"
                placeholder="Select recovery status"
                data={["Created", "Edited", "Reviewed", "Completed"]}
                value={recoveryData.filter.status}
                onChange={(value) => {
                  setRecoveryData({
                    ...recoveryData,
                    filter: {
                      ...recoveryData.filter,
                      status: value,
                    },
                  });
                }}
              /> */}
              <TextInput
                className="mx-2 min-w-[225px]"
                placeholder="Search Party Name"
                icon={<Search size={14} />}
                value={recoveryData.filter.searchText}
                onChange={(e) => {
                  setRecoveryData({
                    ...recoveryData,
                    filter: {
                      ...recoveryData.filter,
                      searchText: e.currentTarget.value,
                    },
                  });
                }}
              />
              {/* <TextInput
                className="mx-2 min-w-[225px]"
                placeholder="Less than amount"
                type="number"
                value={recoveryData.filter.minAmount}
                onChange={(e) => {
                  setRecoveryData({
                    ...recoveryData,
                    filter: {
                      ...recoveryData.filter,
                      minAmount: e.currentTarget.value,
                    },
                  });
                }}
              />
              <TextInput
                className="mx-2 min-w-[225px]"
                placeholder="Greater than amount"
                type="number"
                value={recoveryData.filter.maxAmount}
                onChange={(e) => {
                  setRecoveryData({
                    ...recoveryData,
                    filter: {
                      ...recoveryData.filter,
                      maxAmount: e.currentTarget.value,
                    },
                  });
                }}
              /> */}
              <DateRangePicker
                className="mx-2 min-w-[255px]"
                style={{ minWidth: "255px" }}
                placeholder="select next action date"
                value={recoveryData.filter.nextActionDate}
                onChange={(value) => {
                  setRecoveryData({
                    ...recoveryData,
                    filter: {
                      ...recoveryData.filter,
                      nextActionDate: value,
                    },
                  });
                }}
              />
              <DateRangePicker
                className="mx-2 min-w-[255px]"
                style={{ minWidth: "255px" }}
                placeholder="select last action date"
                value={recoveryData.filter.lastActionDate}
                onChange={(value) => {
                  setRecoveryData({
                    ...recoveryData,
                    filter: {
                      ...recoveryData.filter,
                      lastActionDate: value,
                    },
                  });
                }}
              />
              <RangeSlider
                className="mx-2 min-w-[225px] mr-4"
                labelAlwaysOn
                min={0}
                max={1000000}
                step={100}
                value={[recoveryData.filter.minAmount, recoveryData.filter.maxAmount]}
                onChange={(value) => {
                  console.log('this is value', value);
                  setRecoveryData({
                    ...recoveryData,
                    filter: {
                      ...recoveryData.filter,
                      minAmount: value[0],
                      maxAmount: value[1],
                    },
                  });
                }}
                marks={[
                  { value: 0, label: '0' },
                  { value: 250000, label: '2.5L' },
                  { value: 500000, label: '5L' },
                  { value: 750000, label: '7.5L' },
                  { value: 1000000, label: '10L' },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
      <br />
      <Button className="mx-2" variant="outline" onClick={handleSearchClick}>
        search
      </Button>
      <Button className="mx-2"  onClick={handleClearClick}>
        clear
      </Button>
      <br />
      <br />
      <div className="flex items-center justify-end">
        sort:
        <Select
          placeholder="Sort by"
          className="mx-2"
          value={recoveryData.filter.orderByColumn}
          onChange={(value) => {
            setRecoveryData({
              ...recoveryData,
              filter: {
                ...recoveryData.filter,
                orderByColumn: value,
              },
            });
            fetchRecoverys();
          }}
          data={[
            { label: "reference no.", value: "r.id" },
            { label: "amount", value: "d.recoveryAmount" },
            { label: "name", value: "name" },
            { label: "last action", value: "lastAction" },
            { label: "next action", value: "nextAction" },
          ]}
        />
        <ActionIcon
          onClick={() => {
            setRecoveryData((prevState) => ({
              ...prevState,
              filter: {
                ...prevState.filter,
                sortType: prevState.filter.sortType === "ASC" ? "DESC" : "ASC",
              },
            }));
            fetchRecoverys();
          }}
          color="blue"
          className={recoveryData.filter.sortType === "ASC" ? "" : "rotate-180"}
        >
          <SortAscending />
        </ActionIcon>
        <ActionIcon
          className="ml-5"
          title="Excel Download"
          onClick={handleExcelDownload}
        >
          <Download color="green" />
        </ActionIcon>
      </div>
      <div className="flex flex-col">
        <UniversalTable
          headers={recoveryTableHeaders}
          data={recoveryList}
          loading={false}
          emptyMessage="No recoverys found"
        />
      </div>
      {recoveryData.count > 0 ? (
        <TablePagination
          totalCount={recoveryData.count}
          page={recoveryData.pageNo}
          pageSize={parseInt(recoveryData.pageSize, 10)}
          onPageChange={(page) => {
            setRecoveryData({
              ...recoveryData,
              pageNo: page,
            });
            fetchRecoverys();
          }}
          onPageSizeChange={(pageSize) => {
            setRecoveryData({
              ...recoveryData,
              pageSize: pageSize.toString(),
              pageNo: 1,
            });
            fetchRecoverys();
          }}
          dataLength={recoveryList.length}
        />
      ) : null}
    </>
  );
}
